import { HUError, HUErrorCode } from "@/modules/error/huError"
import { useFetchUser } from "@/modules/users/hooks/useFetchUser"
import { AuthError, LoginNextStep, LoginNextStepError } from "@modules/auth/authErrorTypes"
import { AuthError as AmplifyAuthError, signIn, signOut } from "aws-amplify/auth"

export const useLogin = () => {
  const currentUser = useFetchUser()
  const login = async ({ email, password }: { email: string; password: string }) => {
    let result: Awaited<ReturnType<typeof signIn>>
    try {
      result = await signIn({ username: email, password })
    } catch (e) {
      // A leftover Cognito session (the page's on-mount signOut may not have finished, or another tab
      // is still signed in) makes signIn throw UserAlreadyAuthenticatedException. Clear it and retry
      // once so the user doesn't have to submit twice.
      if (e instanceof AmplifyAuthError && e.name === AuthError.UserAlreadyAuthenticatedException) {
        await signOut()
        result = await signIn({ username: email, password })
      } else {
        throw e
      }
    }

    const step = result.nextStep.signInStep
    if (step === "CONFIRM_SIGN_UP") {
      throw new LoginNextStepError(LoginNextStep.ConfirmSignUp)
    }
    if (step === "RESET_PASSWORD") {
      throw new LoginNextStepError(LoginNextStep.ResetPassword)
    }
    // Any other step (MFA confirmation, forced new password, …) is not supported by this flow.
    // Surface it instead of falling through to currentUser(), which would fail with an opaque error.
    if (step !== "DONE") {
      throw new Error(`Unsupported sign-in step: ${step}`)
    }

    try {
      await currentUser()
    } catch (e) {
      // only retry for the "no token" auth case
      if (e instanceof HUError && e.code === HUErrorCode.Unauthorised) {
        // wait 200ms to retry ; token might not be ready yet
        await new Promise((resolve) => setTimeout(resolve, 200))
        await currentUser()
      } else {
        throw e
      }
    }
  }

  return login
}
